import {
  Heading,
  Image,
  XStack,
  YStack,
  Button,
  ButtonText,
  Text,
  Spacer,
  AtSignIcon,
  HashTagIcon,
} from '@bounty/creators-design-system'
import { createParam } from 'solito'
import { BsFillPlusCircleFill } from 'react-icons/bs'
import { BsPlayCircleFill } from 'react-icons/bs'
import { GetStoreResponseSuccess } from '@bounty/types'
import { useRouter } from 'solito/router'
import InstagramLogo from '../../../../assets/instagram.png'
import TikTokLogo from '../../../../assets/tiktok.png'
import { ComponentType } from 'react'
import { IconProps } from 'tamagui-phosphor-icons'
import { StyleProp, TextStyle } from 'react-native'
import { logger } from '@bounty/creators-common/utils/logger'
import useHyperlinkParser from '../../../../../hooks/src/useHyperlinkParser'

const { useParam } = createParam()

interface ShopBountiesCardProps {
  isShopPage?: boolean
  shopImageSrc: string
  minimumPayout?: number
  shopSlug: string
  shopName: string
  shopBrandedHashTag: string
  brief: GetStoreResponseSuccess['data']['briefs']['0']
  tiktokUsername: string
  instagramUsername: string
}

export const ShopBountiesCard = (props: ShopBountiesCardProps) => {
  const {
    shopName,
    isShopPage = true,
    brief,
    shopImageSrc,
    minimumPayout = 0,
    shopSlug,
    shopBrandedHashTag,
    tiktokUsername,
    instagramUsername,
  } = props
  const [slug] = useParam('slug')
  const navigate = useRouter()
  const { convertHyperlink } = useHyperlinkParser()

  // Temporary Hack: Only show old brief name if brief is not updated
  const oldBriefDetailsDate = 1701555780 * 1000

  if (!slug) {
    throw new Error('Cannot find required param slug!')
  }

  return (
    <YStack
      borderWidth="1px"
      borderColor="$gray.200.light"
      bg="$gray.50.light"
      borderRadius="$2"
      padding="$4"
      gap="$4"
    >
      <XStack display="flex" justifyContent="space-between" alignItems="center">
        <Image
          borderWidth="4px"
          borderColor="black"
          borderRadius="$7"
          width="$9"
          height="$9"
          src={shopImageSrc || ''}
        />
        <XStack
          height="fit-content"
          paddingHorizontal="$3"
          paddingVertical="$1"
          borderRadius="$1"
          borderWidth="1px"
          borderColor="black"
        >
          <Heading size="$3">{`$${minimumPayout || 0} min`}</Heading>
        </XStack>
      </XStack>
      <XStack>
        <Heading size="$xl">
          {oldBriefDetailsDate > new Date(brief?.createdAt).getTime()
            ? `${shopName} - Bounty`
            : brief?.name}
        </Heading>
      </XStack>
      <XStack position="relative">
        <YStack
          bg="$primary.100"
          width="100%"
          borderRadius="$5"
          paddingHorizontal="$3"
          paddingVertical="$2"
        >
          {
            // I know these comments and disabler doesn't look good, but needed.
            // Since for some reason overflowWrap aren't available as props for Text which is very necessary

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-expect-error
            <Text style={{ overflowWrap: 'anywhere' }}>
              {convertHyperlink(brief?.content)}
            </Text>
          }
          {(tiktokUsername || instagramUsername) && <Spacer />}
          {tiktokUsername && (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            <XStack display="flex" gap="$1" alignItems="center">
              <Image
                src={TikTokLogo}
                height={20}
                width={20}
                resizeMode="contain"
              />
              <Text
                style={{ overflowWrap: 'anywhere' } as StyleProp<TextStyle>}
                color="$blackAlpha.600"
              >
                tag {tiktokUsername}.
              </Text>
            </XStack>
          )}
          {instagramUsername && (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            <XStack display="flex" gap="$1" alignItems="center">
              <Image
                src={InstagramLogo}
                height={20}
                width={20}
                resizeMode="contain"
              />
              <Text
                style={{ overflowWrap: 'anywhere' } as StyleProp<TextStyle>}
                color="$blackAlpha.600"
              >
                tag {instagramUsername}.
              </Text>
            </XStack>
          )}
          {shopBrandedHashTag && (
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            <XStack display="flex" gap="$1" alignItems="center">
              <HashTagIcon height={20} width={20} />
              <Text
                style={{ overflowWrap: 'anywhere' } as StyleProp<TextStyle>}
                color="$blackAlpha.600"
              >
                {shopBrandedHashTag}
              </Text>
            </XStack>
          )}
        </YStack>
        <XStack position="absolute" bottom="-5px" left="-5px">
          {/* Didn't separated this since it's not a whole icon */}
          <svg
            width={20}
            height={76}
            viewBox="0 0 20 76"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.195669 75.1846C5.39567 75.9846 10.529 73.1212 12.1957 71.2879C10.4778 67.1914 21.0836 57.2419 14.0836 57.2415C12.465 57.2415 11.0825 53.0014 5.19567 56.1846C5.17446 57.4714 5.19567 61.9258 5.19567 62.6842C5.19567 73.1842 -0.804331 74.5813 0.195669 75.1846Z"
              fill="#DADBFD"
            />
          </svg>
        </XStack>
      </XStack>
      <XStack display="flex" gap="$3">
        {isShopPage ? (
          <>
            <a
              style={{
                flex: 1,
                display: 'flex',
                textDecoration: 'none',
              }}
              target="_blank"
              rel="noreferrer"
              href={brief.videoUrl || 'https://www.tiktok.com/@trybounty'}
            >
              <Button
                flex={1}
                variant="outline"
                colorScheme="primary"
                event={`Clicked ${
                  brief.videoUrl ? 'See example' : 'Content inspo'
                }`}
              >
                <XStack mr="$2" borderColor="">
                  <BsPlayCircleFill />
                </XStack>
                {brief.videoUrl ? 'See example' : 'Content inspo'}
              </Button>
            </a>
            <Button
              flex={1}
              colorScheme="success"
              event="Claim Bounty"
              onPress={() =>
                navigate.push(`/s/${shopSlug}?briefId=${brief?.id}`)
              }
            >
              <XStack mr="$2">
                <BsFillPlusCircleFill fill="$black" />
              </XStack>
              <ButtonText color="$blackAlpha.900" fontWeight="600">
                Claim Bounty
              </ButtonText>
            </Button>
          </>
        ) : (
          <a
            style={{
              flex: 1,
              display: 'flex',
              textDecoration: 'none',
              flexBasis: 'max-content',
            }}
            target="_blank"
            rel="noreferrer"
            href={brief.videoUrl || 'https://www.tiktok.com/@trybounty'}
          >
            <Button
              flex={1}
              variant="outline"
              colorScheme="primary"
              event="Clicked Content Inspo"
            >
              <XStack mr="$2" borderColor="">
                <BsPlayCircleFill />
              </XStack>
              See example
            </Button>
          </a>
        )}
      </XStack>
    </YStack>
  )
}
